// The leads mirror — a read-only local copy of the server's ACTIVE leads (with
// items) so the board and detail views work in a dead zone.
//
// FULL REPLACE, never incremental (docs/plan.md, Reading existing leads
// offline): a few hundred rows of text is not worth a `?since=` cursor, and a
// wholesale replace is self-healing — any drift, any missed update, any
// half-applied write is corrected by the next successful fetch.
//
// Fetched on load, after login and on reconnect. Card scans are deliberately
// NOT part of this mirror (they are the heavy, PII-sensitive part); offline,
// the detail view shows "Card scan needs a connection" in place of the scan.
//
// SEAMS FOR LATER WAVES (board / detail):
//   • readMirror()   — the synced leads. Read this offline.
//   • readDrafts()   — leads captured on THIS device, not yet synced. Merge
//                      these in as "Pending"-ref rows.
//   • getLastSyncAt()— for the board's "Showing leads as of 09:14" line. A
//                      stale list presented as current is worse than no list.
import type { LeadWithItems } from '@paradise/shared';

import { apiRequest } from '@/lib/api/http';
import { db, metaGet, metaSet, type LeadDraftRow, type LeadMirrorRow } from '@/lib/db';

export type MirrorRefreshResult = 'updated' | 'offline' | 'unauthenticated' | 'error';

// Monotonic request/apply tokens guarding the full-replace below. Several
// triggers (boot, reconnect, tab focus, post-drain) can call this
// concurrently; without a token a stale response that lands late (e.g. a slow
// GET fired before a fast one) would clobber a fresher mirror — wiping a lead
// another call just synced in — until the NEXT refresh happened to correct
// it. `leadsDraft` (unsynced drafts) is untouched either way.
let refreshSeq = 0;
let appliedSeq = 0;

/** Fetch active leads and FULL-REPLACE the mirror. Stamps `meta.lastSyncAt` on success. */
export async function refreshLeadsMirror(): Promise<MirrorRefreshResult> {
  const seq = ++refreshSeq;
  const result = await apiRequest<LeadWithItems[]>('/api/leads');
  switch (result.kind) {
    case 'ok':
      if (seq < appliedSeq) {
        // A later-started refresh already landed and was applied; this
        // response is stale — do not resurrect data a fresher fetch already
        // superseded.
        return 'updated';
      }
      appliedSeq = seq;
      await db.transaction('rw', db.leads, db.meta, async () => {
        await db.leads.clear();
        await db.leads.bulkPut(result.data);
        await metaSet('lastSyncAt', Date.now());
      });
      return 'updated';
    case 'offline':
      return 'offline';
    case 'unauthenticated':
      // Not our decision — the session watchers own logout. Leave the mirror in
      // place so the board keeps showing the last-known list offline.
      return 'unauthenticated';
    case 'error':
      return 'error';
  }
}

/** The synced leads, newest-updated first. Board reads this offline. */
export async function readMirror(): Promise<LeadMirrorRow[]> {
  return db.leads.orderBy('updatedAt').reverse().toArray();
}

/**
 * Leads captured on this device that have not synced (still pending, or parked
 * as failed). Merge these into the board with a "Pending" ref. A `synced` draft
 * is intentionally excluded — it now lives in the mirror as a canonical row.
 */
export async function readDrafts(): Promise<LeadDraftRow[]> {
  return db.leadsDraft.where('status').anyOf(['pending_sync', 'failed']).sortBy('createdAt');
}

/** One unsynced draft by `clientUuid` — the detail and edit screens resolve a pending lead through this. */
export async function readDraft(clientUuid: string): Promise<LeadDraftRow | undefined> {
  const row = await db.leadsDraft.get(clientUuid);
  return row && row.status !== 'synced' ? row : undefined;
}

/** Epoch ms of the last successful mirror fetch — the board's freshness line. */
export async function getLastSyncAt(): Promise<number | null> {
  return metaGet<number>('lastSyncAt');
}
